import { useState } from 'react';

import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';

import {
  ESdkEvents,
  MixpanelEvents,
  EPaymentProvider,
  EStripePaymentMethods,
  EBEPaymentMethodsName,
} from '../../../../contracts';
import { sendFEEvent } from '../../../../eventUtil';
import { getPaymentMethodsConfig } from './utils';
import { logAlways } from '../../../../utils/coralogix-rum';
import { useCheckout } from '../../../../hooks/use-checkout';
import { useStripeConfig } from '../../hooks/use-stripe-config';
import {
  expressCheckoutEventToDisplayNameMapWithRouter,
  useStripeSubmit,
} from '../../hooks/use-stripe-submit';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { sendMixpanelEvent } from '../../../../utils/mixpanel-utils';

interface StripeExpressCheckoutProps {
  onReady: (isAvailable: boolean) => void;
  exclusivePaymentMethod?: EStripePaymentMethods;
}

export function StripeExpressCheckoutWrapper(props: StripeExpressCheckoutProps) {
  const { expressElementOptions, expressElementPromise } = useStripeConfig();

  return (
    <Elements stripe={expressElementPromise} options={expressElementOptions}>
      <StripeExpressCheckoutElement {...props} />
    </Elements>
  );
}

export function StripeExpressCheckoutElement({
  onReady,
  exclusivePaymentMethod,
}: StripeExpressCheckoutProps) {
  const { checkoutData, getEventParams, getMixpanelEventParams } = useCheckout();
  const { featureFlags } = useAppConfig();
  const { onExpressElementConfirm } = useStripeSubmit({ setOverlay: () => {} });
  const [hideExpressPaymentElement, setHideExpressPaymentElement] = useState(false);

  const checkoutConfigurations = checkoutData?.checkoutConfigurations;

  const paymentMethods = checkoutData?.paymentMethods;
  const showGoogle =
    Boolean(
      paymentMethods?.find(
        (pm) =>
          pm.paymentMethodName === EBEPaymentMethodsName.google &&
          pm.provider === EPaymentProvider.stripe,
      ),
    ) && !checkoutConfigurations?.shouldShowGooglePayAsApm;
  const showApple = Boolean(
    paymentMethods?.find(
      (pm) =>
        pm.paymentMethodName === EBEPaymentMethodsName.apple &&
        pm.provider === EPaymentProvider.stripe,
    ),
  );

  const showLink =
    Boolean(
      paymentMethods?.find(
        (pm) =>
          pm.paymentMethodName === EBEPaymentMethodsName.link &&
          pm.provider === EPaymentProvider.stripe,
      ),
    ) && !featureFlags?.checkout_display_link_express_as_apm_box;

  if (hideExpressPaymentElement) {
    return null;
  }

  return (
    <ExpressCheckoutElement
      options={{
        paymentMethodOrder: [
          EStripePaymentMethods.apple,
          EStripePaymentMethods.google,
          EStripePaymentMethods.link,
        ],
        paymentMethods: getPaymentMethodsConfig(
          showGoogle,
          showApple,
          showLink,
          exclusivePaymentMethod,
        ),
        layout: {
          overflow: 'never',
          maxColumns: 1,
        },
      }}
      onReady={(e) => {
        if (!e.availablePaymentMethods) {
          setHideExpressPaymentElement(true);
          onReady(false);
        } else {
          onReady(true);
        }
      }}
      onLoadError={(error) => {
        setHideExpressPaymentElement(true);
        onReady(false);
        logAlways('Stripe Express Checkout Element onLoadError', {
          error: JSON.stringify(error),
          checkoutData: JSON.stringify(checkoutData?.orderId),
        });
      }}
      onClick={(event) => {
        const paymentMethodDisplayName =
          expressCheckoutEventToDisplayNameMapWithRouter[event.expressPaymentType] ||
          event.expressPaymentType;
        sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, {
          ...getEventParams(),
          paymentMethodName: paymentMethodDisplayName,
        });
        sendMixpanelEvent(MixpanelEvents.PAYMENT_CHOOSE, {
          ...getMixpanelEventParams(),
          paymentMethodName: paymentMethodDisplayName,
        });
        sendMixpanelEvent(MixpanelEvents.ON_PAY_BUTTON_CLICKED, {
          ...getMixpanelEventParams(),
          paymentMethodName: paymentMethodDisplayName,
        });
        event.resolve({ emailRequired: true });
      }}
      onConfirm={(event) => {
        onExpressElementConfirm(event);
      }}
    />
  );
}
